import type {
  AdminSessionLoginRequest,
  AdminSessionRead,
  AlertRead,
  AnalyticsAudienceRead,
  AnalyticsEnvelope,
  AnalyticsEventCreate,
  AnalyticsEventSummaryRead,
  AnalyticsEventTimeseriesRead,
  AnalyticsTopIpsRead,
  CacheInvalidateReport,
  CacheInvalidateRequest,
  CancellationReport,
  CompareReport,
  CompareRequest,
  CoverageMetric,
  DataSummaryReport,
  DelayHistogram,
  DelaysVsAlertsReport,
  DwellTimeReport,
  ErrorGroupDetail,
  ErrorGroupListResponse,
  ErrorGroupRead,
  ErrorSeverity,
  ErrorSource,
  ErrorStatsRead,
  ErrorStatus,
  FrequencyHeatmap,
  FrontendErrorReport,
  HeadwayReport,
  InternalIpCreate,
  InternalIpListRead,
  ItemEnvelope,
  ListEnvelope,
  MagicLinkConsumeRequest,
  MagicLinkSendRequest,
  MagicLinkSendResponse,
  MeRead,
  NextArrivalRead,
  ObservationFunnel,
  OnTimeReport,
  PredictionAccuracyReport,
  PredictionSnapshotRead,
  RecoveryCodeConsumeRequest,
  ReferenceSyncJobRead,
  RouteDateCoverage,
  RouteRead,
  RouteServiceStatus,
  RouteShapeRead,
  RouteShapesBatchRead,
  SavedTripCreate,
  SavedTripPatch,
  SavedTripRead,
  SavedTripReorder,
  SegmentTimeByDateReport,
  SegmentTimeByDowReport,
  SegmentTimeByServiceReport,
  SegmentTimeReport,
  SessionPayload,
  ReachableStopRead,
  StopEventRead,
  StopRead,
  StopsByRouteRead,
  TodBucket,
  TripDetailRead,
  TripTimeReport,
  TripTimelineReport,
  TripTimingReport,
  VehicleLiveRead,
  WindowParam,
} from '../types';

import { apiClient, getApiBaseUrl } from './client';
import { buildQuery } from './query';

const V1 = '/api/v1';

export interface ListParams {
  limit?: number;
  offset?: number;
}

export interface ListRoutesParams extends ListParams {
  type?: number | number[];
  line_id?: string;
  include_archived?: boolean;
}

export function listRoutes(
  params: ListRoutesParams = {},
  signal?: AbortSignal,
): Promise<ListEnvelope<RouteRead>> {
  return apiClient.get(`${V1}/routes${buildQuery({ ...params })}`, { signal });
}

export function getRoute(id: string, signal?: AbortSignal): Promise<ItemEnvelope<RouteRead>> {
  return apiClient.get(`${V1}/routes/${encodeURIComponent(id)}`, { signal });
}

export function getRouteShape(
  routeId: string,
  signal?: AbortSignal,
): Promise<ItemEnvelope<RouteShapeRead>> {
  return apiClient.get(`${V1}/routes/${encodeURIComponent(routeId)}/shape`, { signal });
}

/**
 * Fetch shapes for many routes in one request. Routes without shape data are
 * omitted from the response — the map just draws nothing for them.
 */
export function getRouteShapes(
  routeIds: string[],
  signal?: AbortSignal,
): Promise<ItemEnvelope<RouteShapesBatchRead>> {
  return apiClient.get(`${V1}/routes/shapes${buildQuery({ route_id: routeIds })}`, { signal });
}

export interface ListStopsParams extends ListParams {
  route_id?: string | string[];
  bbox?: string;
  parent_station_id?: string;
  location_type?: number;
  include_archived?: boolean;
  q?: string;
}

export function listStops(
  params: ListStopsParams = {},
  signal?: AbortSignal,
): Promise<ListEnvelope<StopRead>> {
  return apiClient.get(`${V1}/stops${buildQuery({ ...params })}`, { signal });
}

/**
 * Fetch stops for many routes in one request, each tagged with the routes
 * serving it. Replaces the map's per-route `listStops` fan-out.
 */
export function listStopsByRoute(
  routeIds: string[],
  signal?: AbortSignal,
): Promise<ItemEnvelope<StopsByRouteRead>> {
  return apiClient.get(`${V1}/stops/by-route${buildQuery({ route_id: routeIds })}`, { signal });
}

export function getStop(id: string, signal?: AbortSignal): Promise<ItemEnvelope<StopRead>> {
  return apiClient.get(`${V1}/stops/${encodeURIComponent(id)}`, { signal });
}

/**
 * Stops reachable from `fromStopId` by riding `routeId` a single leg, each
 * tagged with the direction to ride and its stop order. Powers the save
 * flow's destination picker and lets it infer the leg's direction.
 */
export function listReachableStops(
  routeId: string,
  fromStopId: string,
  signal?: AbortSignal,
): Promise<ListEnvelope<ReachableStopRead>> {
  return apiClient.get(
    `${V1}/stops/reachable${buildQuery({ route_id: routeId, from_stop_id: fromStopId })}`,
    { signal },
  );
}

export function getTrip(id: string, signal?: AbortSignal): Promise<ItemEnvelope<TripDetailRead>> {
  return apiClient.get(`${V1}/trips/${encodeURIComponent(id)}`, { signal });
}

export type LiveVehiclesMode = 'subway' | 'commuter_rail' | 'light_rail' | 'heavy_rail';

export interface LiveVehiclesParams {
  route_id?: string | string[];
  bbox?: string;
  /**
   * One or more modes. Sent as repeated query params (`mode=subway&mode=commuter_rail`)
   * when an array is supplied — the backend unions the route_type filters.
   */
  mode?: LiveVehiclesMode | LiveVehiclesMode[];
  trip_id?: string;
}

export function listLiveVehicles(
  params: LiveVehiclesParams = {},
  signal?: AbortSignal,
): Promise<ListEnvelope<VehicleLiveRead>> {
  return apiClient.get(`${V1}/live/vehicles${buildQuery({ ...params })}`, { signal });
}

export interface LivePredictionsParams {
  stop_id?: string | string[];
  route_id?: string | string[];
  trip_id?: string;
  direction_id?: number;
  limit?: number;
}

export function listLivePredictions(
  params: LivePredictionsParams,
  signal?: AbortSignal,
): Promise<ListEnvelope<PredictionSnapshotRead>> {
  return apiClient.get(`${V1}/live/predictions${buildQuery({ ...params })}`, { signal });
}

export interface LiveAlertsParams {
  route_id?: string | string[];
  stop_id?: string | string[];
  effect?: string;
  severity?: number;
  lifecycle?: string;
  include_archived?: boolean;
}

export function listLiveAlerts(
  params: LiveAlertsParams = {},
  signal?: AbortSignal,
): Promise<ListEnvelope<AlertRead>> {
  return apiClient.get(`${V1}/live/alerts${buildQuery({ ...params })}`, { signal });
}

export function getServiceStatus(signal?: AbortSignal): Promise<ListEnvelope<RouteServiceStatus>> {
  return apiClient.get(`${V1}/live/service-status`, { signal });
}

export interface NextArrivalsParams {
  limit?: number;
}

export function listNextArrivals(
  stopId: string,
  params: NextArrivalsParams = {},
  signal?: AbortSignal,
): Promise<ListEnvelope<NextArrivalRead>> {
  return apiClient.get(
    `${V1}/live/stops/${encodeURIComponent(stopId)}/next${buildQuery({ ...params })}`,
    { signal },
  );
}

export interface StopEventsParams extends ListParams {
  trip_id?: string;
  route_id?: string;
  stop_id?: string;
  service_date_from?: string;
  service_date_to?: string;
  status?: string;
}

export function listStopEvents(
  params: StopEventsParams,
  signal?: AbortSignal,
): Promise<ListEnvelope<StopEventRead>> {
  return apiClient.get(`${V1}/stop-events${buildQuery({ ...params })}`, { signal });
}

// ---------------------------------------------------------------------------
// Data summary — simple, auditable data-health snapshot (admin-gated)
// ---------------------------------------------------------------------------

// The data-trust views default to a "trustworthy-from" lower bound (plan
// §9.6); pass `includeUntrusted` to reach the full pre-fix history. The param
// is only sent when true so the default URLs stay clean.
function untrusted(includeUntrusted?: boolean): { include_untrusted?: true } {
  return includeUntrusted ? { include_untrusted: true } : {};
}

export function getDataSummary(
  includeUntrusted?: boolean,
  signal?: AbortSignal,
): Promise<ItemEnvelope<DataSummaryReport>> {
  return apiClient.get(`${V1}/admin/data/summary${buildQuery(untrusted(includeUntrusted))}`, {
    signal,
  });
}

export function getObservationFunnel(
  includeUntrusted?: boolean,
  signal?: AbortSignal,
): Promise<ItemEnvelope<ObservationFunnel>> {
  return apiClient.get(
    `${V1}/admin/data/observation-funnel${buildQuery(untrusted(includeUntrusted))}`,
    { signal },
  );
}

export function getRouteDateCoverage(
  metric: CoverageMetric = 'trips',
  includeUntrusted?: boolean,
  signal?: AbortSignal,
): Promise<ItemEnvelope<RouteDateCoverage>> {
  return apiClient.get(
    `${V1}/admin/data/route-date-coverage${buildQuery({ metric, ...untrusted(includeUntrusted) })}`,
    { signal },
  );
}

export function getPredictionAccuracy(
  routeId?: string,
  window?: WindowParam,
  includeUntrusted?: boolean,
  signal?: AbortSignal,
): Promise<ItemEnvelope<PredictionAccuracyReport>> {
  return apiClient.get(
    `${V1}/admin/data/prediction-accuracy${buildQuery({
      route_id: routeId,
      window,
      ...untrusted(includeUntrusted),
    })}`,
    { signal },
  );
}

export function getTripTiming(
  window?: WindowParam,
  includeUntrusted?: boolean,
  signal?: AbortSignal,
): Promise<ItemEnvelope<TripTimingReport>> {
  return apiClient.get(
    `${V1}/admin/data/trip-timing${buildQuery({ window, ...untrusted(includeUntrusted) })}`,
    { signal },
  );
}

export interface AnalyticsParams {
  window: WindowParam;
  tod_bucket?: TodBucket;
}

export interface OnTimeParams extends AnalyticsParams {
  route_id?: string | string[];
  stop_id?: string;
  direction_id?: number;
  group_by?: 'route' | 'stop' | 'route_tod';
}

export function getOnTime(
  params: OnTimeParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<OnTimeReport>> {
  return apiClient.get(`${V1}/analytics/on-time${buildQuery({ ...params })}`, { signal });
}

export interface HeadwaysParams extends AnalyticsParams {
  route_id?: string;
  stop_id?: string;
  direction_id?: number;
  detail?: 'stats' | 'samples' | 'both';
}

export function getHeadways(
  params: HeadwaysParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<HeadwayReport>> {
  return apiClient.get(`${V1}/analytics/headways${buildQuery({ ...params })}`, { signal });
}

export interface TripTimesParams extends AnalyticsParams {
  route_id?: string;
  direction_id?: number;
  detail?: 'stats' | 'samples' | 'both';
}

export function getTripTimes(
  params: TripTimesParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<TripTimeReport>> {
  return apiClient.get(`${V1}/analytics/trip-times${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface SegmentTimesParams extends AnalyticsParams {
  from_stop: string;
  to_stop: string;
  route_id?: string;
  direction_id?: number;
  detail?: 'stats' | 'samples' | 'both';
}

export function getSegmentTimes(
  params: SegmentTimesParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<SegmentTimeReport>> {
  return apiClient.get(`${V1}/analytics/segment-times${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface SegmentTimesByDowParams extends AnalyticsParams {
  from_stop: string;
  to_stop: string;
  route_id?: string;
  direction_id?: number;
  /** Bucket width in minutes. Server accepts 15, 30, 60; defaults to 30. */
  bucket_minutes?: 15 | 30 | 60;
}

export function getSegmentTimesByDow(
  params: SegmentTimesByDowParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<SegmentTimeByDowReport>> {
  return apiClient.get(`${V1}/analytics/segment-times/by-dow${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface SegmentTimesByDateParams extends AnalyticsParams {
  from_stop: string;
  to_stop: string;
  route_id?: string;
  direction_id?: number;
  /** Boston-local minutes-since-midnight commute-window bounds. */
  depart_after_minute?: number;
  depart_before_minute?: number;
  /** Station-aware sibling expansion. Server default true. */
  station_aware?: boolean;
  /** Reach pre-trustworthy-from history. Server default false. */
  include_untrusted?: boolean;
}

export function getSegmentTimesByDate(
  params: SegmentTimesByDateParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<SegmentTimeByDateReport>> {
  return apiClient.get(`${V1}/analytics/segment-times/by-date${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface SegmentTimesByServiceParams extends AnalyticsParams {
  from_stop: string;
  to_stop: string;
  route_id?: string;
  direction_id?: number;
  /** Boston-local minutes-since-midnight commute-window bounds. */
  depart_after_minute?: number;
  depart_before_minute?: number;
  /** Station-aware sibling expansion. Server default true. */
  station_aware?: boolean;
  /** Reach pre-trustworthy-from history. Server default false. */
  include_untrusted?: boolean;
}

export function getSegmentTimesByService(
  params: SegmentTimesByServiceParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<SegmentTimeByServiceReport>> {
  return apiClient.get(`${V1}/analytics/segment-times/by-service${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface DelayDistributionParams extends AnalyticsParams {
  route_id?: string;
  bin_seconds?: number;
}

export function getDelayDistribution(
  params: DelayDistributionParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<DelayHistogram>> {
  return apiClient.get(`${V1}/analytics/delay-distribution${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface CancellationsParams extends AnalyticsParams {
  route_id?: string | string[];
}

export function getCancellations(
  params: CancellationsParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<CancellationReport>> {
  return apiClient.get(`${V1}/analytics/cancellations${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface DelaysVsAlertsParams extends AnalyticsParams {
  route_id?: string;
}

export function getDelaysVsAlerts(
  params: DelaysVsAlertsParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<DelaysVsAlertsReport>> {
  return apiClient.get(`${V1}/analytics/delays-vs-alerts${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface FrequencyHeatmapParams extends AnalyticsParams {
  route_id: string;
}

export function getFrequencyHeatmap(
  params: FrequencyHeatmapParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<FrequencyHeatmap>> {
  return apiClient.get(`${V1}/analytics/frequency-heatmap${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface DwellTimesParams extends AnalyticsParams {
  route_id?: string;
  stop_id?: string;
  direction_id?: number;
  detail?: 'stats' | 'samples' | 'both';
}

export function getDwellTimes(
  params: DwellTimesParams,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<DwellTimeReport>> {
  return apiClient.get(`${V1}/analytics/dwell-times${buildQuery({ ...params })}`, {
    signal,
  });
}

export function getTripTimeline(
  tripId: string,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<TripTimelineReport>> {
  return apiClient.get(`${V1}/analytics/trip/${encodeURIComponent(tripId)}/timeline`, { signal });
}

export function compareAnalytics(
  body: CompareRequest,
  signal?: AbortSignal,
): Promise<AnalyticsEnvelope<CompareReport>> {
  return apiClient.post(`${V1}/analytics/compare`, body, { signal });
}

export function getAdminSession(signal?: AbortSignal): Promise<AdminSessionRead> {
  return apiClient.get(`${V1}/admin/session`, { signal });
}

export function adminLogin(body: AdminSessionLoginRequest): Promise<AdminSessionRead> {
  return apiClient.post(`${V1}/admin/session`, body);
}

export function adminLogout(): Promise<void> {
  return apiClient.del(`${V1}/admin/session`);
}

export function refreshReference(): Promise<ItemEnvelope<ReferenceSyncJobRead>> {
  return apiClient.post(`${V1}/admin/refresh-reference`);
}

export function getRefreshJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<ItemEnvelope<ReferenceSyncJobRead>> {
  return apiClient.get(`${V1}/admin/refresh-reference/jobs/${encodeURIComponent(jobId)}`, {
    signal,
  });
}

export function listRefreshJobs(
  limit = 20,
  signal?: AbortSignal,
): Promise<ListEnvelope<ReferenceSyncJobRead>> {
  return apiClient.get(`${V1}/admin/refresh-reference/jobs${buildQuery({ limit })}`, {
    signal,
  });
}

export function invalidateCache(
  body: CacheInvalidateRequest,
): Promise<ItemEnvelope<CacheInvalidateReport>> {
  return apiClient.post(`${V1}/admin/cache/invalidate`, body);
}

export interface AdminAnalyticsParams {
  window: WindowParam;
}

export interface AdminAnalyticsTimeseriesParams extends AdminAnalyticsParams {
  bucket?: 'hour' | 'day';
}

export function getAdminAnalyticsSummary(
  params: AdminAnalyticsParams,
  signal?: AbortSignal,
): Promise<ItemEnvelope<AnalyticsEventSummaryRead>> {
  return apiClient.get(`${V1}/admin/analytics/summary${buildQuery({ ...params })}`, {
    signal,
  });
}

export function getAdminAnalyticsTimeseries(
  params: AdminAnalyticsTimeseriesParams,
  signal?: AbortSignal,
): Promise<ItemEnvelope<AnalyticsEventTimeseriesRead>> {
  return apiClient.get(`${V1}/admin/analytics/timeseries${buildQuery({ ...params })}`, {
    signal,
  });
}

export interface AdminAudienceParams {
  window: WindowParam;
  include_internal?: boolean;
}

export function getAdminAudience(
  params: AdminAudienceParams,
  signal?: AbortSignal,
): Promise<ItemEnvelope<AnalyticsAudienceRead>> {
  return apiClient.get(`${V1}/admin/analytics/audience${buildQuery({ ...params })}`, {
    signal,
  });
}

export function getAdminTopIps(
  params: AdminAnalyticsParams,
  signal?: AbortSignal,
): Promise<ItemEnvelope<AnalyticsTopIpsRead>> {
  return apiClient.get(`${V1}/admin/analytics/top-ips${buildQuery({ ...params })}`, {
    signal,
  });
}

export function getAdminInternalIps(
  signal?: AbortSignal,
): Promise<ItemEnvelope<InternalIpListRead>> {
  return apiClient.get(`${V1}/admin/analytics/internal-ips`, { signal });
}

export function addAdminInternalIp(
  body: InternalIpCreate,
): Promise<ItemEnvelope<InternalIpListRead>> {
  return apiClient.post(`${V1}/admin/analytics/internal-ips`, body);
}

export function removeAdminInternalIp(ipHash: string): Promise<ItemEnvelope<InternalIpListRead>> {
  return apiClient.del(`${V1}/admin/analytics/internal-ips/${encodeURIComponent(ipHash)}`);
}

export function postEvent(body: AnalyticsEventCreate): Promise<void> {
  return apiClient.post(`${V1}/events`, body);
}

// ---------------------------------------------------------------------------
// Error monitoring
// ---------------------------------------------------------------------------

export interface ListErrorGroupsParams {
  status?: ErrorStatus;
  source?: ErrorSource;
  severity?: ErrorSeverity;
  search?: string;
  window?: WindowParam;
  sort?: 'last_seen' | 'first_seen' | 'count';
  limit?: number;
  offset?: number;
}

export function listErrorGroups(
  params: ListErrorGroupsParams = {},
  signal?: AbortSignal,
): Promise<ErrorGroupListResponse> {
  return apiClient.get(`${V1}/admin/errors/groups${buildQuery({ ...params })}`, { signal });
}

export function getErrorGroup(
  groupId: number,
  signal?: AbortSignal,
): Promise<ItemEnvelope<ErrorGroupDetail>> {
  return apiClient.get(`${V1}/admin/errors/groups/${String(groupId)}`, { signal });
}

export interface ErrorGroupStatusUpdate {
  notes?: string | null;
}

export function resolveErrorGroup(
  groupId: number,
  body: ErrorGroupStatusUpdate = {},
): Promise<ItemEnvelope<ErrorGroupRead>> {
  return apiClient.post(`${V1}/admin/errors/groups/${String(groupId)}/resolve`, body);
}

export function ignoreErrorGroup(
  groupId: number,
  body: ErrorGroupStatusUpdate = {},
): Promise<ItemEnvelope<ErrorGroupRead>> {
  return apiClient.post(`${V1}/admin/errors/groups/${String(groupId)}/ignore`, body);
}

export function reopenErrorGroup(
  groupId: number,
  body: ErrorGroupStatusUpdate = {},
): Promise<ItemEnvelope<ErrorGroupRead>> {
  return apiClient.post(`${V1}/admin/errors/groups/${String(groupId)}/reopen`, body);
}

export interface ErrorStatsParams {
  window: WindowParam;
  bucket?: 'hour' | 'day';
}

export function getErrorStats(
  params: ErrorStatsParams,
  signal?: AbortSignal,
): Promise<ItemEnvelope<ErrorStatsRead>> {
  return apiClient.get(`${V1}/admin/errors/stats${buildQuery({ ...params })}`, { signal });
}

export function postErrorReport(body: FrontendErrorReport): Promise<void> {
  return apiClient.post(`${V1}/errors/report`, body);
}

export interface HealthStatus {
  ok: boolean;
  status: number;
  detail?: string | null;
}

async function fetchHealth(path: string, signal?: AbortSignal): Promise<HealthStatus> {
  const url = `${getApiBaseUrl()}${path}`;
  const response = await fetch(url, { credentials: 'include', signal });
  if (response.ok) {
    return { ok: true, status: response.status };
  }
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === 'string') {
      detail = body.detail;
    }
  } catch {
    detail = null;
  }
  return { ok: false, status: response.status, detail };
}

export function getHealthz(signal?: AbortSignal): Promise<HealthStatus> {
  return fetchHealth('/healthz', signal);
}

export function getReadyz(signal?: AbortSignal): Promise<HealthStatus> {
  return fetchHealth('/readyz', signal);
}

export async function getMetricsText(signal?: AbortSignal): Promise<string> {
  const response = await fetch(`${getApiBaseUrl()}/metrics`, { credentials: 'include', signal });
  if (!response.ok) {
    throw new Error(`metrics fetch failed: ${response.status}`);
  }
  return response.text();
}

// ---------------------------------------------------------------------------
// Commuter v1 — user auth + saved stop
// ---------------------------------------------------------------------------

export function getMe(signal?: AbortSignal): Promise<MeRead> {
  return apiClient.get(`${V1}/auth/me`, { signal });
}

export function sendMagicLink(body: MagicLinkSendRequest): Promise<MagicLinkSendResponse> {
  return apiClient.post(`${V1}/auth/magic-link/send`, body);
}

export function consumeMagicLink(body: MagicLinkConsumeRequest): Promise<SessionPayload> {
  return apiClient.post(`${V1}/auth/magic-link/consume`, body);
}

export function consumeRecoveryCode(body: RecoveryCodeConsumeRequest): Promise<SessionPayload> {
  return apiClient.post(`${V1}/auth/recovery-code/consume`, body);
}

export function signout(): Promise<void> {
  return apiClient.post(`${V1}/auth/signout`);
}

export function listSavedTrips(signal?: AbortSignal): Promise<SavedTripRead[]> {
  return apiClient.get(`${V1}/me/saved-trips`, { signal });
}

export function createSavedTrip(body: SavedTripCreate): Promise<SavedTripRead> {
  return apiClient.post(`${V1}/me/saved-trips`, body);
}

export function patchSavedTrip(id: number, body: SavedTripPatch): Promise<SavedTripRead> {
  return apiClient.patch(`${V1}/me/saved-trips/${String(id)}`, body);
}

export function deleteSavedTrip(id: number): Promise<void> {
  return apiClient.del(`${V1}/me/saved-trips/${String(id)}`);
}

export function reorderSavedTrip(id: number, body: SavedTripReorder): Promise<SavedTripRead> {
  return apiClient.patch(`${V1}/me/saved-trips/${String(id)}/reorder`, body);
}
